import { z } from 'zod';
import { answerCard, microsoft365Item } from './microsoft365.js';
import { microsoft365WriteCard, microsoft365WriteOutcome } from './microsoft365-write.js';

/**
 * Conversation event log (ADR-0004).
 *
 * Durable events are stored in `conversation_events` with a per-conversation
 * increasing `seq` and are the source of truth. Ephemeral events (token deltas)
 * live only in the Redis stream; the consolidated text arrives later as a
 * durable event, so live and replayed views end up identical (QW-BUG-003).
 */
const attachment = z.object({ fileId: z.uuid(), name: z.string(), size: z.number().int().nonnegative(), mediaType: z.string() });

const usage = z.object({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  costMicros: z.number().int().nonnegative(),
  modelCalls: z.number().int().nonnegative(),
  toolCalls: z.number().int().nonnegative().optional(),
  durationMs: z.number().int().nonnegative().optional(),
});

const todoItem = z.object({
  id: z.string().min(1).max(500),
  content: z.string().min(1).max(2000),
  status: z.enum(['pending', 'in_progress', 'completed']),
  blockedBy: z.array(z.string()).optional(),
});

const question = z.object({
  question: z.string().min(1).max(2000),
  header: z.string().max(60).optional(),
  multiSelect: z.boolean().optional(),
  options: z.array(z.object({ label: z.string().min(1).max(200), description: z.string().max(1000).optional() })).max(8),
});

/** D-178: what a connection's tool call sends; the input stays JSON text (stored payloads would have their keys rewritten) */
export const sentArguments = z.object({
  connection: z.object({ name: z.string(), tool: z.string() }),
  readOnly: z.boolean().optional(),
  arguments: z.string(),
  schema: z.string().optional(),
  truncated: z.object({ shown: z.number().int().nonnegative(), total: z.number().int().nonnegative() }).optional(),
});
export type SentArgumentsDto = z.infer<typeof sentArguments>;

const turnUsage = z.object({
  /** the last model call's prompt, i.e. how full the context is now */
  promptTokens: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  contextWindow: z.number().int().nonnegative(),
  modelCalls: z.number().int().nonnegative(),
  model: z.string().optional(),
});

/**
 * 16b.1: why a turn ended without an answer (`no_visible_reply`), for its card and the steps line: every reply hit the
 * output limit (in the thinking or inside a tool call) or came back empty. Counts and times only, never content.
 */
export const OUTPUT_CUTS = ['thinking', 'tool_call'] as const;
const turnFailure = z.object({
  cause: z.enum(['output_limit', 'empty']),
  /** where the last try was cut */
  cut: z.enum(OUTPUT_CUTS).optional(),
  /** the tool whose call was cut and the file it was writing */
  tool: z.string().max(64).optional(),
  path: z.string().max(300).optional(),
  /** each try: how long it took, whether and where the limit cut it, the tokens it wrote */
  attempts: z
    .array(z.object({ ms: z.number().int().nonnegative(), cut: z.enum(OUTPUT_CUTS).nullable(), outputTokens: z.number().int().nonnegative().nullable() }))
    .min(1)
    .max(20),
  /** the output limit of one reply */
  maxTokens: z.number().int().positive().optional(),
  /** a file of the workspace changed during the turn (parts written before the stop) */
  changed: z.boolean(),
});
export const turnFailureSchema = turnFailure;
export type TurnFailureDto = z.infer<typeof turnFailure>;
export type OutputCut = (typeof OUTPUT_CUTS)[number];

const contextCategory = z.object({ key: z.string(), tokens: z.number().int().nonnegative() });
/** a skill loaded in the conversation and what it weighs in the prompt (Phase 11) */
const loadedSkill = z.object({ name: z.string(), tokens: z.number().int().nonnegative() });
const skillRef = z.object({ key: z.string().max(100), name: z.string().max(64), args: z.string().max(4000) });

export const TURN_END_REASONS = ['completed', 'failed', 'cancelled', 'rejected', 'limit', 'interrupted'] as const;
export const APPROVAL_MODES = ['ask', 'auto_edit', 'plan', 'full'] as const;

/** A document passage the model was given, under the number it may cite (Phase 12, DOC-004). */
const passage = z.object({
  n: z.number().int().positive(),
  documentId: z.uuid(),
  documentName: z.string().max(255),
  chunkId: z.uuid().nullable(),
  kind: z.enum(['page', 'sheet', 'slide', 'section']),
  number: z.number().int(),
  label: z.string().max(500).nullable(),
  rowFrom: z.number().int().nullable(),
  rowTo: z.number().int().nullable(),
});
const citation = passage.extend({ quote: z.string().max(400) });
export type DocPassage = z.infer<typeof passage>;
export type DocCitation = z.infer<typeof citation>;

export const durableEventPayloads = {
  'user.message': z.object({
    messageId: z.uuid(),
    text: z.string(),
    attachments: z.array(attachment).optional(),
    /** QW-1205: the message runs a skill (`/name arguments`) */
    skill: skillRef.optional(),
    /**
     * v0.16c.6 (D-416): "Yeni mail olarak hazırla" on a reply card Microsoft could not open: the run prepares a new
     * mail card from that card (its approval id) before the model is asked anything
     */
    microsoft365: z.object({ replyAsNew: z.uuid() }).strict().optional(),
  }),
  'turn.started': z.object({
    runId: z.uuid(),
    trigger: z.enum(['user', 'continue', 'system', 'compact', 'regenerate']).optional(),
    resumed: z.boolean().optional(),
    /** the user messages this turn answers, delivered at its start (QW-102) */
    inputSeqs: z.array(z.number().int().positive()).optional(),
    /** a note for the model that the user did not type (continue after a limit, resume) */
    note: z.string().optional(),
  }),
  /** Messages and background results delivered at a tool boundary (QW-103, QW-811). */
  'input.delivered': z.object({
    runId: z.uuid(),
    seqs: z.array(z.number().int().positive()),
    notifications: z
      .array(z.object({ taskId: z.uuid(), label: z.string(), status: z.string(), result: z.string().optional() }))
      .optional(),
  }),
  /** A step's visible text; reasoning is stored and sent back to the model, shown folded (D-26). */
  'assistant.message': z.object({
    messageId: z.uuid(),
    runId: z.uuid(),
    text: z.string(),
    stepId: z.string().optional(),
    reasoning: z.string().optional(),
    /** D-77: with reasoning, how long the visible text took (first token to the end); the rest of the step was reasoning */
    writingMs: z.number().int().nonnegative().optional(),
    /** DOC-004: the passages the answer cites by number, with the sentence that supports it */
    citations: z.array(citation).max(200).optional(),
    /**
     * D-197 (Phase 15e): Microsoft 365 answer cards. The model's `oppy-kart` blocks are taken out
     * of `text` and resolved on the server from Graph data by ref; `at` is where each goes in `text`.
     */
    cards: z.array(answerCard).max(3).optional(),
  }),
  'turn.completed': z.object({
    runId: z.uuid(),
    reason: z.enum(TURN_END_REASONS),
    errorCode: z.string().optional(),
    /** D-01: which limit stopped the turn; the UI offers "continue" */
    limit: z.object({ kind: z.enum(['tool_calls', 'steps', 'repeat']), used: z.number().int(), max: z.number().int() }).optional(),
    /** SA-023 (D-97): the token budget that ran out during the turn, when it resets, its use */
    quota: z
      .object({ scope: z.enum(['user_daily', 'tenant_monthly']), resetsAt: z.string().max(40), used: z.number().nonnegative(), limit: z.number().nonnegative() })
      .optional(),
    /** QW-611, MM-008: tokens of this turn and the window */
    usage: turnUsage.optional(),
    /** 16b.1: why a `no_visible_reply` turn got no answer */
    failure: turnFailure.optional(),
  }),
  'system.note': z.object({ code: z.string(), params: z.record(z.string(), z.string()).optional() }),
  /**
   * 16b.1: the output limit of one reply cut it, in the thinking or inside a tool call. The rest of the turn runs without
   * thinking, also after a pause for the user (a new worker pass reads it here). Counts and times only, never content.
   */
  'output.cut': z.object({
    runId: z.uuid(),
    cut: z.enum(OUTPUT_CUTS),
    maxTokens: z.number().int().positive(),
    outputTokens: z.number().int().nonnegative().nullable(),
    ms: z.number().int().nonnegative(),
    tool: z.string().max(64).optional(),
  }),
  /**
   * A tool call (06 §12). `title` and `summary` are plain language for every
   * user; `details` is technical (command, code) and chat spaces never show
   * it (D-35, SA-068). `arguments` keeps the model's call for the history.
   */
  'tool.started': z.object({
    runId: z.uuid(),
    callId: z.string(),
    tool: z.string(),
    title: z.string(),
    summary: z.string(),
    details: z.string().optional(),
    stepId: z.string().optional(),
    arguments: z.string().optional(),
    /**
     * 16b.1: the reply's output limit cut this call; it never runs (its result tells the model to write in parts) and
     * `arguments` keeps a short, complete version. How long the call was and the limit it reached.
     */
    cut: z.object({ chars: z.number().int().nonnegative(), maxTokens: z.number().int().positive() }).optional(),
    /**
     * v0.16c.6 (D-416): the run made this call itself for the person's message with this seq ("Yeni mail olarak
     * hazırla"), not the model; once per message
     */
    fromMessage: z.number().int().positive().optional(),
  }),
  /** The call passed the permission check and is running now (ADR-0005: at most once). */
  'tool.executing': z.object({
    runId: z.uuid(),
    callId: z.string(),
    /** QW-BUG-056: a command that ran without asking because a loaded skill granted it */
    grantedBy: z.string().max(64).optional(),
    /** D-179: why a connection's tool ran without asking */
    allowedBy: z.enum(['trusted_read', 'tool_policy', 'rule', 'full_access']).optional(),
  }),
  'tool.completed': z.object({
    runId: z.uuid(),
    callId: z.string(),
    tool: z.string(),
    ok: z.boolean(),
    errorCode: z.string().optional(),
    artifactIds: z.array(z.uuid()).optional(),
    /** what the model received (already shortened, secrets masked) */
    result: z.string().optional(),
    durationMs: z.number().int().nonnegative().optional(),
    /** Phase 15e: a built-in tool's outcome in plain words for the steps line ("Gelen kutusu okundu · 24 mail") */
    label: z.string().max(160).optional(),
    /**
     * part 3 (D-233): the result holds content copied from Microsoft 365 (doc_search or doc_read passages of a document
     * from OneDrive, SharePoint or an Outlook attachment): it counts as Microsoft 365 content for the locked cards and
     * the links of the answer, not for the words-only document search
     */
    sources: z.array(z.literal('microsoft365')).max(1).optional(),
  }),
  /** A file produced in the conversation, downloadable as an artifact (QW-1009, SA-068). */
  'artifact.created': z.object({
    artifactId: z.uuid(),
    runId: z.uuid(),
    name: z.string(),
    kind: z.string(),
    mediaType: z.string(),
    size: z.number().int().nonnegative(),
    version: z.number().int().positive(),
  }),
  /** An approval card, a plan card or a question (06 §10.2, §12; D-25, D-29). */
  'approval.requested': z.object({
    approvalId: z.uuid(),
    runId: z.uuid(),
    kind: z.enum(['tool', 'plan', 'question']),
    callId: z.string(),
    tool: z.string(),
    title: z.string(),
    summary: z.string(),
    details: z.string().optional(),
    /** D-178: a connection's tool call: what it sends, as JSON text, with the schema's field names */
    sent: sentArguments.optional(),
    /** D-180: a tool that always asks; only "allow once" and "reject" */
    locked: z.boolean().optional(),
    effects: z.array(z.string()).optional(),
    options: z.array(z.object({ id: z.string(), label: z.string(), kind: z.enum(['allow', 'reject', 'other']) })),
    plan: z.string().optional(),
    questions: z.array(question).optional(),
    expiresAt: z.string().optional(),
    /** set when a sub-agent asks; its writes go through the parent's approval (D-02) */
    subagent: z.object({ runId: z.uuid(), description: z.string() }).optional(),
    /** Microsoft 365 part 3: a mail or meeting card (payload, hash, view); decided only on its own send route */
    microsoft365: microsoft365WriteCard.optional(),
  }),
  'approval.resolved': z.object({
    approvalId: z.uuid(),
    runId: z.uuid(),
    status: z.enum(['approved', 'rejected', 'expired', 'cancelled']),
    option: z.string().optional(),
    answers: z.record(z.string(), z.string()).optional(),
    /** Microsoft 365 part 3: how a write card ended (sent, created, failed, unknown, expired, cancelled, stopped) */
    microsoft365: microsoft365WriteOutcome.optional(),
  }),
  'mode.changed': z.object({
    mode: z.enum(APPROVAL_MODES),
    previous: z.enum(APPROVAL_MODES),
    reason: z.enum(['user', 'plan_approved', 'allow_all_edits', 'plan_requested']),
  }),
  /** The task list (QW-805, D-19); the latest event is the current list. */
  'todo.updated': z.object({ runId: z.uuid(), items: z.array(todoItem) }),
  /** D-07: the plan the user approved (possibly edited), turned into the task list. */
  'plan.approved': z.object({ runId: z.uuid(), plan: z.string(), mode: z.enum(APPROVAL_MODES), edited: z.boolean() }),
  'subagent.started': z.object({
    runId: z.uuid(),
    callId: z.string(),
    subRunId: z.uuid(),
    description: z.string(),
    subagentType: z.string().optional(),
    background: z.boolean(),
  }),
  'subagent.completed': z.object({
    runId: z.uuid(),
    callId: z.string(),
    subRunId: z.uuid(),
    status: z.enum(['completed', 'failed', 'cancelled', 'interrupted']),
    stopReason: z.string(),
    usage,
  }),
  /** Background tasks (SA-020, QW-709, QW-812): status changes, also "stopped by a restart". */
  'task.updated': z.object({
    taskId: z.uuid(),
    kind: z.enum(['shell', 'subagent']),
    label: z.string(),
    status: z.enum(['running', 'completed', 'failed', 'cancelled', 'stopped_by_restart']),
    summary: z.string().optional(),
  }),
  /** D-06: a server started listening in the sandbox; the UI opens the preview. */
  /** D-06; D-99: the turn whose command started the server, so its line sits in that turn */
  'preview.detected': z.object({ port: z.number().int().min(1).max(65535), runId: z.uuid().optional() }),
  /**
   * Rewind (QW-106, QW-1306, D-22): events from `fromSeq` up to this one leave
   * the conversation (the model's history and the view); the remaining turns keep
   * their reasoning (QW-BUG-010). Edit and regenerate are rewinds too (D-40).
   */
  'conversation.rewound': z.object({
    fromSeq: z.number().int().positive(),
    reason: z.enum(['rewind', 'edit', 'regenerate']),
    files: z.boolean(),
    restored: z.object({ filesChanged: z.number().int().nonnegative(), notRestorable: z.array(z.string()) }).optional(),
  }),
  /** QW-203: title, pin, archive, group and model changes reach every client of the conversation */
  'conversation.updated': z.object({
    title: z.string().optional(),
    titleSource: z.enum(['manual', 'auto']).optional(),
    pinned: z.boolean().optional(),
    archived: z.boolean().optional(),
    groupId: z.uuid().nullable().optional(),
    modelRef: z.uuid().nullable().optional(),
    thinking: z.enum(['auto', 'off']).optional(),
  }),
  /** QW-205: the first event of a branch */
  'conversation.forked': z.object({ fromConversationId: z.uuid(), fromSeq: z.number().int().nonnegative(), title: z.string().optional() }),
  /** ADR-0010, QW-114: older events were summarised; the hidden state is not in the event (D-08) */
  'context.compacted': z.object({
    compactionId: z.uuid(),
    throughSeq: z.number().int().nonnegative(),
    reason: z.enum(['auto', 'manual', 'overflow']),
    tokensBefore: z.number().int().nonnegative().optional(),
    tokensAfter: z.number().int().nonnegative().optional(),
    summary: z.string(),
  }),
  /** the workspace before the first change of a turn (QW-106, rewind with files) */
  'snapshot.created': z.object({ runId: z.uuid(), snapshotId: z.string() }),
  /** D-16, SA-061: something was saved to memory because the user asked; visible and removable */
  'memory.saved': z.object({ memoryId: z.uuid(), content: z.string(), scope: z.enum(['user', 'workspace']) }),
  /**
   * QW-1203…QW-1205, D-03: a skill's instructions were loaded. Its grants hold
   * while this run lasts. A skill the user ran with `/name` carries its text
   * here (the model reads it after the user's message; clients show a chip).
   */
  'skill.loaded': z.object({
    runId: z.uuid(),
    callId: z.string().optional(),
    skillKey: z.string().max(100),
    versionId: z.uuid().optional(),
    name: z.string().max(64),
    level: z.enum(['builtin', 'organization', 'team', 'user', 'workspace']),
    version: z.number().int().positive().optional(),
    part: z.number().int().positive().optional(),
    parts: z.number().int().positive().optional(),
    tokens: z.number().int().nonnegative(),
    invokedBy: z.enum(['model', 'user']),
    dir: z.string().max(200).optional(),
    grants: z.object({ commands: z.array(z.string().max(300)).max(50), ownScripts: z.boolean() }),
    text: z.string().optional(),
    /** the skill was already in the context: nothing was added, its grants hold again for this run (QW-1216) */
    reused: z.boolean().optional(),
    /** `/name`: the user message that ran it */
    messageSeq: z.number().int().positive().optional(),
  }),
  /** QW-BUG-050: skills or connected services changed during the session; the model is told once */
  'capabilities.changed': z.object({
    added: z.array(z.object({ kind: z.enum(['skill', 'mcp_server', 'mcp_tool']), name: z.string() })).max(200),
    removed: z.array(z.object({ kind: z.enum(['skill', 'mcp_server', 'mcp_tool']), name: z.string() })).max(200),
  }),
  /** DOC-013: a web page the model read; the conversation's sources */
  'source.added': z.object({ runId: z.uuid(), callId: z.string().optional(), url: z.string().max(4096), title: z.string().max(500).optional(), accessedAt: z.string() }),
  /** Phase 12: passages a document search or read gave the model, numbered for citations (DOC-004) */
  'doc.passages': z.object({ runId: z.uuid(), callId: z.string(), passages: z.array(passage).max(100) }),
  /**
   * Phase 15e (D-196): mails, events, attachments and lists a Microsoft 365 tool gave the model,
   * under short refs (m1, e1, a1, l1) that stay the same through the conversation; the facts
   * (sender, subject, times, attendees, file names, sizes) come from Graph and feed the answer cards.
   */
  'microsoft.items': z.object({ runId: z.uuid(), callId: z.string(), items: z.array(microsoft365Item).max(120) }),
  /** D-92: documents are still being read; the model starts only when they are ready */
  'docs.waiting': z.object({ runId: z.uuid(), documents: z.array(z.object({ id: z.uuid(), name: z.string().max(255) })).max(50) }),
  /** D-92: the wait ended: each document's state, and how long it took */
  'docs.ready': z.object({
    runId: z.uuid(),
    documents: z.array(z.object({ id: z.uuid(), name: z.string().max(255), status: z.enum(['ready', 'failed', 'pending']) })).max(50),
    waitedMs: z.number().int().nonnegative(),
  }),
} as const;

export const ephemeralEventPayloads = {
  'assistant.delta': z.object({ messageId: z.uuid(), runId: z.uuid(), text: z.string() }),
  'reasoning.delta': z.object({ messageId: z.uuid(), runId: z.uuid(), text: z.string() }),
  /** D-05: the provider is busy; the turn retries visibly */
  'model.retry': z.object({ runId: z.uuid(), attempt: z.number().int(), max: z.number().int(), delayMs: z.number().int(), reason: z.string() }),
  /** D-06: files changed in the workspace; an open preview refreshes */
  'files.changed': z.object({ runId: z.uuid(), paths: z.array(z.string()).max(200) }),
  /** live output of a running tool (shell, run_code); code workspaces show it, chat spaces do not */
  'tool.output': z.object({ runId: z.uuid(), callId: z.string(), stream: z.enum(['stdout', 'stderr']), text: z.string() }),
  /** QW-301, QW-311: each model call's prompt and what it is made of; the ring shows the last */
  'context.usage': z.object({
    runId: z.uuid(),
    promptTokens: z.number().int().nonnegative(),
    exact: z.boolean(),
    contextWindow: z.number().int().nonnegative(),
    categories: z.array(contextCategory),
    /** the skills loaded in the conversation, each with its share of the prompt */
    skills: z.array(loadedSkill).optional(),
  }),
  /** compaction is running (the next answer waits for it) */
  'context.compacting': z.object({ runId: z.uuid(), reason: z.enum(['auto', 'manual', 'overflow']) }),
} as const;

export type DurableEventType = keyof typeof durableEventPayloads;
export type EphemeralEventType = keyof typeof ephemeralEventPayloads;
export type ConversationEventType = DurableEventType | EphemeralEventType;

export type DurablePayload<T extends DurableEventType> = z.infer<(typeof durableEventPayloads)[T]>;
export type EphemeralPayload<T extends EphemeralEventType> = z.infer<(typeof ephemeralEventPayloads)[T]>;

export const DURABLE_EVENT_TYPES = Object.keys(durableEventPayloads) as DurableEventType[];
export const EPHEMERAL_EVENT_TYPES = Object.keys(ephemeralEventPayloads) as EphemeralEventType[];

export function isDurableEventType(type: string): type is DurableEventType {
  return Object.hasOwn(durableEventPayloads, type);
}

export function isEphemeralEventType(type: string): type is EphemeralEventType {
  return Object.hasOwn(ephemeralEventPayloads, type);
}

/** A durable event as delivered to clients (SSE `data:` and JSON pages). */
export interface DurableEvent {
  kind: 'durable';
  seq: number;
  type: DurableEventType;
  payload: Record<string, unknown>;
  createdAt: string;
}

/** An ephemeral event; `afterSeq` is the last durable seq written before it. */
export interface EphemeralEvent {
  kind: 'ephemeral';
  type: EphemeralEventType;
  payload: Record<string, unknown>;
  afterSeq: number;
}

export type ConversationEvent = DurableEvent | EphemeralEvent;

export function parseDurablePayload<T extends DurableEventType>(type: T, payload: unknown): DurablePayload<T> {
  return durableEventPayloads[type].parse(payload) as DurablePayload<T>;
}

/** Redis stream key for a conversation, prefixed by tenant (06 §6). */
export function conversationStreamKey(tenantId: string, conversationId: string): string {
  return `t:${tenantId}:conv:${conversationId}`;
}

/** Interactive run queue (ADR-0005); consumed by the worker. */
export const RUN_QUEUE_INTERACTIVE = 'runs:interactive';
export const RUN_QUEUE_GROUP = 'workers';

/** Document processing (Phase 12, ADR-0011): an entry names a document; its state lives in Postgres. */
export const DOCUMENT_QUEUE = 'documents:process';

export interface DocumentJob {
  documentId: string;
  tenantId: string;
  userId: string;
}

/** Background runs (sub-agents in the background, notification turns) yield to interactive ones. */
export const RUN_QUEUE_BACKGROUND = 'runs:background';

/**
 * A queue entry names a run; its state lives in Postgres (ADR-0005). The
 * same entry starts, resumes (after an approval) or cancels the run.
 */
export interface RunJob {
  runId: string;
  tenantId: string;
  userId: string;
  conversationId: string;
  locale: string;
}
